import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, User, Video, Trash2, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CATS, hhmm } from "./constants";

export function EventDetailDialog({ detail, onClose, onDeleteBlock, onDeleteSlot, onRepeatSlot }) {
  const { t, i18n } = useTranslation();
  const LOC = i18n.language || "en";
  const [weeks, setWeeks] = useState(4);
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[0.7rem] sm:max-w-md">
        {detail && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${CATS[detail.category].dot}`} /> {detail.title}
              </DialogTitle>
              <DialogDescription>{t(CATS[detail.category].labelKey)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center gap-2 text-slate-600"><Clock size={15} className="text-slate-400" />
                {detail.start.toLocaleDateString(LOC, { weekday: "long", month: "long", day: "numeric" })} · {hhmm(detail.start, LOC)} – {hhmm(detail.end, LOC)}
              </div>
              {detail.subtitle && <div className="flex items-center gap-2 text-slate-600"><User size={15} className="text-slate-400" /> {t("cal.withLabel")} {detail.subtitle}</div>}
              {detail.kind === "booking" && (
                <div className="flex items-center gap-2 text-slate-600">
                  <span className="text-slate-400">{t("cal.statusLabel")}:</span>
                  <span className="font-medium">{t(`cal.${detail.data.status === "confirmed" ? "confirmed" : detail.data.status === "completed" ? "completed" : "pending"}`)}</span>
                </div>
              )}
              {detail.kind === "booking" && detail.data.meeting_link && (
                <a href={detail.data.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-indigo-600 hover:underline"><Video size={15} /> {t("cal.meetingLink")}</a>
              )}
              {detail.kind === "avail" && !detail.data.booked && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="repeat-panel">
                  <p className="text-sm font-medium flex items-center gap-1.5 text-slate-700"><Repeat size={14} className="text-indigo-500" /> {t("cal.repeatTitle")}</p>
                  <p className="text-xs text-slate-500">{t("cal.repeatHint")}</p>
                  <div className="flex items-center gap-2">
                    <select data-testid="repeat-weeks" value={weeks} onChange={(e) => setWeeks(+e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30">
                      {[2, 4, 8, 12].map((n) => <option key={n} value={n}>{t("cal.repeatWeeks", { count: n })}</option>)}
                    </select>
                    <Button data-testid="repeat-apply" size="sm" className="rounded-lg gap-1.5" onClick={() => onRepeatSlot?.(weeks)}>
                      <Repeat size={14} /> {t("cal.repeatApply")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              {detail.kind === "block" && (
                <Button data-testid="detail-delete-block" variant="outline" className="rounded-lg gap-1.5 text-rose-600 hover:text-rose-600" onClick={() => onDeleteBlock(detail.data.id)}><Trash2 size={15} /> {t("cal.deleteBlock")}</Button>
              )}
              {detail.kind === "avail" && (
                <Button data-testid="detail-delete-slot" variant="outline" className="rounded-lg gap-1.5 text-rose-600 hover:text-rose-600" onClick={() => onDeleteSlot(detail.data.id)}><Trash2 size={15} /> {t("cal.deleteSlot")}</Button>
              )}
              <Button className="rounded-lg" onClick={onClose}>{t("common.close")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
